/**
 * Backup — export, validate, merge.
 *
 * Pure functions over a document; the UI layer performs the write, through `importEntities`.
 *
 * **A backup carries all eight members of `AppDocument`, not five (#93).** It carried five until
 * this commit: `trust`, `sharedEntries` and `_schemaVersion` were dropped on export and could not
 * be restored on import, so exporting, resetting storage from Settings and re-importing lost your
 * trust map and share registry with no warning at any step. Commit 2 of this step is what made
 * that legible — unifying on `AppDocument` added exactly those three members to the type this file
 * takes.
 *
 * **Validation rejects rather than waves through.** It used to check the top-level shape and that
 * each entity map was an object, and nothing else: a hand-edited file with `version: 99` passed,
 * and `entries: { x: 'not-an-object' }` passed and was copied straight into the CRDT, which then
 * synced to every device.
 *
 * The three new members are optional on `BackupData`, so a file written before this change still
 * imports.
 */

import type { AppDocument, SharedEntryRef, TrustEntry } from './schema'
import type { Entry, Topic, Area, Project, Link } from './schema'
import { CURRENT_SCHEMA_VERSION } from './migration/orchestrator'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

const ENTITY_KEYS = ['entries', 'topics', 'areas', 'projects', 'links'] as const

/** The keyed members that merge under the same skip-existing rule as the entity maps. */
const KEYED_KEYS = [...ENTITY_KEYS, 'trust', 'sharedEntries'] as const

type KeyedKey = (typeof KEYED_KEYS)[number]

/**
 * How many of each member a backup holds.
 *
 * `trust` and `sharedEntries` are optional, because a file written before #93 carries neither and
 * still has to parse. `exportBackup` always writes all seven.
 */
export type BackupCounts = Record<(typeof ENTITY_KEYS)[number], number> &
  Partial<Record<'trust' | 'sharedEntries', number>>

export interface BackupData {
  entries: Record<string, Entry>
  topics: Record<string, Topic>
  areas: Record<string, Area>
  projects: Record<string, Project>
  links: Record<string, Link>
  /** Optional: a backup written before #93 carries neither, and still imports. */
  trust?: Record<string, TrustEntry>
  sharedEntries?: Record<string, SharedEntryRef>
  /** The document's own stamp. The `schemaVersion` header cannot be read back into a document. */
  _schemaVersion?: number
}

function zeroCounts(): BackupCounts {
  return {
    entries: 0,
    topics: 0,
    areas: 0,
    projects: 0,
    links: 0,
    trust: 0,
    sharedEntries: 0,
  }
}

export interface BackupFile {
  version: 1
  schemaVersion: number
  exportedAt: string
  counts: BackupCounts
  data: BackupData
}

export interface MergeResult {
  toImport: BackupData
  imported: BackupCounts
  skipped: BackupCounts
}

type ValidateSuccess = { valid: true; backup: BackupFile }
type ValidateFailure = { valid: false; error: string }
export type ValidateResult = ValidateSuccess | ValidateFailure

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export function exportBackup(doc: AppDocument): BackupFile {
  const schemaVersion = doc._schemaVersion ?? CURRENT_SCHEMA_VERSION

  const data: BackupData = {
    entries: { ...doc.entries },
    topics: { ...doc.topics },
    areas: { ...doc.areas },
    projects: { ...doc.projects },
    links: { ...doc.links },
    trust: { ...doc.trust },
    sharedEntries: { ...doc.sharedEntries },
    _schemaVersion: schemaVersion,
  }

  const counts = zeroCounts()
  for (const key of KEYED_KEYS) {
    counts[key] = Object.keys(data[key] ?? {}).length
  }

  return {
    version: 1,
    schemaVersion,
    exportedAt: new Date().toISOString(),
    counts,
    data,
  }
}

// ---------------------------------------------------------------------------
// Validate
// ---------------------------------------------------------------------------

const BACKUP_FORMAT_VERSION = 1

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function validateBackup(data: unknown): ValidateResult {
  if (data === null || typeof data !== 'object') {
    return { valid: false, error: 'Backup must be a JSON object' }
  }

  const obj = data as Record<string, unknown>

  if (obj.version !== BACKUP_FORMAT_VERSION) {
    return {
      valid: false,
      error: `Unrecognised backup version ${String(obj.version)} — this app writes version ${String(BACKUP_FORMAT_VERSION)}`,
    }
  }

  if (typeof obj.schemaVersion !== 'number') {
    return { valid: false, error: 'Missing or invalid schemaVersion' }
  }

  if (obj.schemaVersion > CURRENT_SCHEMA_VERSION) {
    return {
      valid: false,
      error: `Backup schema version ${String(obj.schemaVersion)} is newer than current (${String(CURRENT_SCHEMA_VERSION)})`,
    }
  }

  if (obj.data === null || typeof obj.data !== 'object') {
    return { valid: false, error: 'Missing data field' }
  }

  const backupData = obj.data as Record<string, unknown>

  for (const key of ENTITY_KEYS) {
    if (!isRecord(backupData[key])) {
      return { valid: false, error: `Missing or invalid data.${key}` }
    }
  }

  // The two optional members: absent is fine, present and malformed is not
  for (const key of ['trust', 'sharedEntries'] as const) {
    if (backupData[key] !== undefined && !isRecord(backupData[key])) {
      return { valid: false, error: `Invalid data.${key}` }
    }
  }

  // Every value inside a keyed map must be an object. Without this a hand-edited file reaches
  // `mergeBackup`'s copy and its garbage is written into the CRDT, which then syncs everywhere.
  for (const key of KEYED_KEYS) {
    const map = backupData[key]
    if (map === undefined) continue

    for (const [id, value] of Object.entries(map as Record<string, unknown>)) {
      if (!isRecord(value)) {
        return { valid: false, error: `data.${key}.${id} is not an object` }
      }
    }
  }

  // Entries carry the minimum shape every reader assumes
  for (const [id, entry] of Object.entries(
    backupData.entries as Record<string, unknown>,
  )) {
    const record = entry as Record<string, unknown>
    if (typeof record.id !== 'string') {
      return { valid: false, error: `data.entries.${id} has no id` }
    }
    if (typeof record.type !== 'string') {
      return { valid: false, error: `data.entries.${id} has no type` }
    }
  }

  return { valid: true, backup: data as BackupFile }
}

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------

export function parseBackupPreview(backup: BackupFile): BackupCounts {
  return { ...backup.counts }
}

// ---------------------------------------------------------------------------
// Merge
// ---------------------------------------------------------------------------

export function mergeBackup(doc: AppDocument, backup: BackupFile): MergeResult {
  const toImport: BackupData = {
    entries: {},
    topics: {},
    areas: {},
    projects: {},
    links: {},
    trust: {},
    sharedEntries: {},
  }
  const imported = zeroCounts()
  const skipped = zeroCounts()

  // Merge-only, skip-existing — the rule the five entity maps already had, now applied to all
  // seven keyed members, so `trust` and `sharedEntries` restore the same way (#93)
  for (const key of KEYED_KEYS) {
    const localMap = (doc[key] ?? {}) as Record<string, unknown>
    const backupMap = (backup.data[key] ?? {}) as Record<string, unknown>

    for (const id of Object.keys(backupMap)) {
      if (id in localMap) {
        skipped[key]++
      } else {
        ;(toImport[key] as Record<string, unknown>)[id] = backupMap[id]
        imported[key]++
      }
    }
  }

  return { toImport, imported, skipped }
}

// ---------------------------------------------------------------------------
// Download
// ---------------------------------------------------------------------------

export function downloadBackup(backup: BackupFile): void {
  const json = JSON.stringify(backup, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)

  const date = backup.exportedAt.slice(0, 10)
  const filename = `konstantout-backup-${date}.json`

  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
